<template>
  <div class="payment-status-polling">
    <div v-if="status === 'success'" class="payment-status-polling__success">
      <div class="payment-status-polling__circle">
        <CheckIcon color="#ffffff" />
      </div>
      <span class="payment-status-polling__text">Сплачено</span>
    </div>
    <p v-else-if="status === 'error'" class="payment-status-polling__error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script>
import { invoiceStatuses, invoiceTypes } from '@/entities/insurance/config';
import { CheckIcon } from '@/shared/ui/icons';

const MAX_CONSECUTIVE_ERRORS = 5;

export default {
  components: { CheckIcon },
  props: {
    orderId: { type: [String, Number], required: true },
    invoiceType: { type: Number, default: invoiceTypes.INSURANCE_PAYMENT },
    paymentsData: { type: Array, default: () => [] },
    paymentInvoiceId: { default: null },
    isPaid: { type: Boolean, default: false },
    paymentCheckResult: { type: Object, default: null },
  },
  data() {
    return {
      interval: null,
      status: null,
      errorMessage: '',
      consecutiveErrors: 0,
    };
  },
  watch: {
    paymentsData: {
      handler(payments) {
        if (!payments?.length) return;

        if (this.isPaid) {
          this.status = 'success';
          this.stopPolling();
          return;
        }

        const invoiceId = this.$route?.query?.invoice || this.paymentInvoiceId;
        if (invoiceId) {
          this.startPolling();
        }
      },
    },
    paymentCheckResult(result) {
      if (!result) return;

      if (result.error) {
        this.consecutiveErrors++;
        if (this.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          this.status = 'error';
          this.errorMessage = result.error;
          this.stopPolling();
        }
        return;
      }

      this.consecutiveErrors = 0;

      if (result.statusId === invoiceStatuses.PAID) {
        this.status = 'success';
        this.stopPolling();
        this.$emit('set-payment-invoice-id', null);
        this.$emit('set-payment-system-code', null);
        this.$emit('fetch-payments', this.orderId);
        this.$emit('processed', { invoiceId: result.invoiceId, statusId: invoiceStatuses.PAID });
      }
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    startPolling() {
      this.stopPolling();
      this.status = null;
      this.errorMessage = '';
      this.consecutiveErrors = 0;

      const invoiceId = this.$route?.query?.invoice || this.paymentInvoiceId;

      if (invoiceId) {
        this.pollInvoiceId(invoiceId);
      } else {
        this.interval = setInterval(this.poll, 2000);
      }
    },
    stopPolling() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    pollInvoiceId(invoiceId) {
      this.interval = setInterval(() => {
        this.$emit('check-payment', invoiceId);
      }, 2000);
    },
    poll() {
      const payments = this.paymentsData;
      if (!payments?.length) {
        this.$emit('fetch-payments', this.orderId);
        return;
      }

      const invoices = payments
        .filter(p => p.invoice?.type_id === this.invoiceType)
        .map(p => p.invoice);

      if (!invoices.length) return;

      for (const invoice of invoices) {
        this.$emit('check-payment', invoice.id);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.payment-status-polling {
  display: flex;
  justify-content: center;
  align-items: center;

  &__success {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__circle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: #2AA63C;
  }

  &__text {
    font-size: 16px;
    color: #2AA63C;
    font-weight: 500;
  }

  &__error {
    font-size: 16px;
    font-weight: 500;
    color: #FF3B30;
    margin: 0;
  }
}
</style>